import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, shortSku, shortWax, fmtDate, STATUS_LABEL } from './wickTestApi';
import './WickTesting.css';

/**
 * Wick Testing — the management side.
 *
 * Coverage: every vessel + fragrance-oil combo in the live catalog (from the
 *   BOM mirror) and whether it has an approved wick on the current wax.
 *   Pick combos -> "Queue for testing" -> candidates are generated (usually
 *   3: the best guess plus one size down and one up).
 * Queue: what Joleen needs to pour, what is poured, what is in the lab.
 *   Print the pour sheet and the QR labels from here.
 * Results: approved and failed rounds.
 * Settings: current wax, how many candidates, pass/fail limits.
 */

const TABS = [
  { id: 'coverage', label: 'Coverage' },
  { id: 'queue', label: 'Queue' },
  { id: 'results', label: 'Results' },
  { id: 'settings', label: 'Settings' },
];

export default function WickTesting() {
  const [tab, setTab] = useState('coverage');
  const [coverage, setCoverage] = useState(null);
  const [requests, setRequests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cov, reqs, st] = await Promise.all([api.coverage(), api.requests(), api.settings()]);
      setCoverage(cov);
      setRequests(reqs);
      setSettings(st);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCount = requests.filter((r) => ['queued', 'poured', 'testing'].includes(r.status)).length;

  return (
    <div className="wt-app">
      <div className="wt-header">
        <div>
          <h1>Wick Testing</h1>
          <p className="wt-sub">
            Burn qualification per vessel, fragrance and wax.
            {coverage && <> Current wax: <strong>{shortWax(coverage.current_wax_sku)}</strong>.</>}
          </p>
        </div>
        <div className="wt-toolbar">
          <Link className="wt-btn secondary" to="/production/wick-test">Lab form</Link>
          <Link className="wt-btn secondary" to="/production/wick-pour">Pour station</Link>
          <button className="wt-btn secondary" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <div className="wt-error">{error}</div>}

      <div className="wt-tabs">
        {TABS.map((t) => (
          <button key={t.id} className={`wt-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
            {t.id === 'coverage' && coverage && <span className="wt-count">{coverage.counts?.untested || 0} untested</span>}
            {t.id === 'queue' && <span className="wt-count">{openCount}</span>}
          </button>
        ))}
      </div>

      {tab === 'coverage' && coverage && <CoverageTab coverage={coverage} settings={settings} onQueued={load} />}
      {tab === 'queue' && <QueueTab requests={requests} onChange={load} />}
      {tab === 'results' && <ResultsTab requests={requests} />}
      {tab === 'settings' && settings && <SettingsTab settings={settings} onSaved={(s) => { setSettings(s); load(); }} />}
    </div>
  );
}

// ── Coverage ────────────────────────────────────────────────────────────────

function CoverageTab({ coverage, settings, onQueued }) {
  const [status, setStatus] = useState('untested');
  const [brand, setBrand] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [queueing, setQueueing] = useState(false);
  const [preview, setPreview] = useState(null);

  const rows = coverage.rows || [];
  const brands = useMemo(() => [...new Set(rows.map((r) => r.brand).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (brand && r.brand !== brand) return false;
      if (needle) {
        const hay = [r.vessel_sku, r.oil_sku, ...(r.product_skus || []), ...(r.product_names || [])].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, status, brand, q]);

  const toggle = (key) => setSelected((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.key));
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allVisibleSelected) filtered.forEach((r) => n.delete(r.key));
    else filtered.forEach((r) => n.add(r.key));
    return n;
  });

  const openPreview = async () => {
    const picks = rows.filter((r) => selected.has(r.key) && r.status !== 'approved' && !r.open_request_id);
    if (!picks.length) return;
    setPreview({ picks, suggestions: {}, loading: true });
    const suggestions = {};
    await Promise.all(picks.slice(0, 40).map(async (p) => {
      try { suggestions[p.key] = await api.suggest(p.vessel_sku, p.oil_sku); } catch (e) { suggestions[p.key] = { error: e.message }; }
    }));
    setPreview({ picks, suggestions, loading: false });
  };

  const queueSelected = async () => {
    if (!preview) return;
    setQueueing(true);
    try {
      const combos = preview.picks.map((p) => ({ vessel_sku: p.vessel_sku, oil_sku: p.oil_sku }));
      const out = await api.createRequests({ combos });
      setPreview(null);
      setSelected(new Set());
      onQueued();
      if (out.skipped?.length) {
        window.setTimeout(() => window.alert(`${out.created.length} queued. Skipped ${out.skipped.length}: ${out.skipped.map((s) => `${shortSku(s.vessel_sku)} / ${shortSku(s.oil_sku)} (${s.reason})`).join('; ')}`), 50);
      }
    } catch (e) {
      window.alert(e.response?.data?.error || e.message);
    } finally {
      setQueueing(false);
    }
  };

  const c = coverage.counts || {};
  const tiles = [
    ['untested', 'Not tested on this wax'],
    ['queued', 'Queued to pour'],
    ['poured', 'Poured'],
    ['testing', 'In the lab'],
    ['approved', 'Approved'],
    ['failed', 'Failed round'],
  ];

  return (
    <>
      <div className="wt-tiles">
        <button className={`wt-tile ${status === '' ? 'active' : ''}`} onClick={() => setStatus('')}>
          <div className="wt-tile-n">{coverage.total}</div>
          <div className="wt-tile-l">All combos</div>
        </button>
        {tiles.map(([k, l]) => (
          <button key={k} className={`wt-tile ${status === k ? 'active' : ''}`} onClick={() => setStatus(k)}>
            <div className="wt-tile-n">{c[k] || 0}</div>
            <div className="wt-tile-l">{l}</div>
          </button>
        ))}
      </div>

      <div className="wt-toolbar">
        <input className="wt-input" placeholder="Search vessel, oil, SKU, product…" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 260 }} />
        <select className="wt-select" value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">All brands</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <span className="wt-note">{filtered.length} combos · {selected.size} selected</span>
        <span className="wt-spacer" />
        <button className="wt-btn" disabled={!selected.size} onClick={openPreview}>Queue selected for testing</button>
      </div>

      <div className="wt-table-wrap">
        <table className="wt-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} /></th>
              <th>Status</th>
              <th>Brand</th>
              <th>Vessel</th>
              <th>Fragrance</th>
              <th>Size</th>
              <th>Products</th>
              <th>BOM wick</th>
              <th>BOM wax</th>
              <th>Approved wick</th>
              <th>Prior wax</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.key}>
                <td><input type="checkbox" checked={selected.has(r.key)} onChange={() => toggle(r.key)} disabled={r.status === 'approved' || !!r.open_request_id} /></td>
                <td>
                  <span className={`wt-badge ${r.status}`}>{STATUS_LABEL[r.status] || r.status}</span>
                  {r.open_request_code && <div className="mono wt-muted">{r.open_request_code}</div>}
                  {r.bom_mismatch && <div><span className="wt-badge warn">BOM ≠ approved</span></div>}
                </td>
                <td>{r.brand || <span className="wt-muted">—</span>}</td>
                <td>{shortSku(r.vessel_sku)}{r.is_metal_vessel && <span className="wt-muted"> (metal)</span>}</td>
                <td>{shortSku(r.oil_sku)}</td>
                <td>{r.size_label || <span className="wt-muted">—</span>}</td>
                <td className="mono">{(r.product_skus || []).join(', ')}</td>
                <td>{(r.bom_wicks || []).map(shortSku).join(' | ')}</td>
                <td>
                  {(r.bom_waxes || []).map(shortWax).join(' | ')}
                  {!r.bom_on_current_wax && <div><span className="wt-badge warn">BOM not on current wax</span></div>}
                </td>
                <td>
                  {r.approved_wick_sku ? shortSku(r.approved_wick_sku) : <span className="wt-muted">—</span>}
                  {r.approved_at && <div className="wt-muted">{fmtDate(r.approved_at)}{r.approved_source === 'drive_import' ? ' · from Drive' : ''}</div>}
                  {r.approved_source_ref && <div><a href={r.approved_source_ref} target="_blank" rel="noreferrer" className="wt-link-btn">sheet</a></div>}
                </td>
                <td>{r.prior_wax_wick_sku ? <>{shortSku(r.prior_wax_wick_sku)} <span className="wt-muted">on {shortWax(r.prior_wax_sku)}</span></> : <span className="wt-muted">—</span>}</td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={11} className="wt-muted" style={{ padding: 20 }}>Nothing matches.</td></tr>}
          </tbody>
        </table>
      </div>

      {preview && (
        <div className="wt-modal-overlay" onClick={() => !queueing && setPreview(null)}>
          <div className="wt-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Queue {preview.picks.length} combo{preview.picks.length === 1 ? '' : 's'} for testing</h2>
            <p className="wt-note">
              Each gets {settings?.candidates_per_request || 3} candidate wicks: the best guess (previous approval, else the BOM wick, else what this vessel usually takes), one size down and one size up.
              You can add or remove candidates from the Queue tab before Joleen pours.
            </p>
            {preview.loading && <div className="wt-note">Working out candidates…</div>}
            {!preview.loading && preview.picks.map((p) => {
              const s = preview.suggestions[p.key];
              return (
                <div key={p.key} className="wt-combo-preview">
                  <div className="title">{shortSku(p.vessel_sku)} · {shortSku(p.oil_sku)} {p.size_label && <span className="wt-muted">({p.size_label})</span>}</div>
                  <div className="meta">{(p.product_skus || []).join(', ')}</div>
                  {s?.error && <div className="wt-error">{s.error}</div>}
                  {s && !s.error && (
                    <div className="wt-cands" style={{ marginTop: 6 }}>
                      {s.candidates.length ? s.candidates.map((c) => (
                        <span key={c.wick_label} className={`wt-cand ${c.role} ${c.stocked ? '' : 'unstocked'}`} title={c.reason}>
                          {c.wick_label}{!c.stocked && ' (not stocked)'}
                        </span>
                      )) : <span className="wt-badge warn">No wick to start from — add candidates by hand after queueing</span>}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="wt-modal-actions">
              <button className="wt-btn secondary" onClick={() => setPreview(null)} disabled={queueing}>Cancel</button>
              <button className="wt-btn" onClick={queueSelected} disabled={queueing || preview.loading}>{queueing ? 'Queueing…' : 'Queue for testing'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Queue ───────────────────────────────────────────────────────────────────

function QueueTab({ requests, onChange }) {
  const [filter, setFilter] = useState('open');
  const [selected, setSelected] = useState(() => new Set());
  const [wicks, setWicks] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.wicks().then(setWicks).catch(() => {}); }, []);

  const groups = {
    open: ['queued', 'poured', 'testing'],
    queued: ['queued'],
    poured: ['poured'],
    testing: ['testing'],
  };
  const rows = requests.filter((r) => groups[filter].includes(r.status));
  const ids = [...selected].filter((id) => rows.some((r) => r.id === id));

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const labelsHref = `/production/wick-test-labels?ids=${(ids.length ? ids : rows.map((r) => r.id)).join(',')}`;

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); await onChange(); } catch (e) { window.alert(e.response?.data?.error || e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="wt-toolbar">
        <div className="wt-tabs" style={{ marginBottom: 0 }}>
          {[['open', 'All open'], ['queued', 'To pour'], ['poured', 'Poured'], ['testing', 'In the lab']].map(([k, l]) => (
            <button key={k} className={`wt-tab ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>{l}<span className="wt-count">{requests.filter((r) => groups[k].includes(r.status)).length}</span></button>
          ))}
        </div>
        <span className="wt-spacer" />
        <Link className="wt-btn secondary" to="/production/wick-pour" target="_blank">Print pour sheet</Link>
        <Link className="wt-btn" to={labelsHref} target="_blank">Print labels{ids.length ? ` (${ids.length})` : ' (all shown)'}</Link>
      </div>

      {!rows.length && <div className="wt-note" style={{ padding: 20 }}>Nothing here. Queue combos from the Coverage tab.</div>}

      {rows.map((r) => (
        <div key={r.id} className="wt-card">
          <div className="wt-card-head">
            <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
            <span className="code">{r.code}</span>
            <span className="title">{shortSku(r.vessel_sku)} · {shortSku(r.oil_sku)}</span>
            {r.size_label && <span className="wt-muted">{r.size_label}</span>}
            <span className={`wt-badge ${r.status}`}>{STATUS_LABEL[r.status]}</span>
            {r.priority > 0 && <span className="wt-badge warn">Priority</span>}
          </div>
          <div className="wt-card-meta">
            {r.product_name} · {(r.product_skus || []).join(', ')} · wax {shortWax(r.wax_sku)}
            {r.bom_wick_sku && <> · BOM wick {shortSku(r.bom_wick_sku)}</>}
            {r.prior_approved_wick_sku && <> · approved {shortSku(r.prior_approved_wick_sku)} on {shortWax(r.prior_approved_wax_sku)}</>}
            {r.poured_at && <> · poured {fmtDate(r.poured_at)}{r.poured_by ? ` by ${r.poured_by}` : ''}</>}
            {r.notes && <> · {r.notes}</>}
          </div>
          <div className="wt-cands">
            {(r.candidates || []).map((c) => (
              <span key={c.id} className={`wt-cand ${c.role} ${c.wick_sku ? '' : 'unstocked'}`} title={c.reason || ''}>
                <span className="wt-cand-code">{c.code}</span>
                {c.wick_label}
                {!c.wick_sku && <span className="wt-muted">(not stocked)</span>}
                <span className={`wt-badge ${c.status}`}>{c.status.replace('_', ' ')}</span>
                {c.status === 'planned' && (
                  <button className="wt-link-btn" title="Remove" disabled={busy} onClick={() => act(() => api.removeCandidate(c.id))}>×</button>
                )}
              </span>
            ))}
          </div>
          <div className="wt-card-actions">
            <AddCandidate wicks={wicks} disabled={busy} onAdd={(body) => act(() => api.addCandidate(r.id, body))} />
            {r.status === 'queued' && (
              <button className="wt-btn small secondary" disabled={busy} onClick={() => {
                const by = window.prompt('Poured by (initials)?', '') || '';
                act(() => api.markPoured(r.id, by));
              }}>Mark poured</button>
            )}
            <button className="wt-btn small secondary" disabled={busy} onClick={() => act(() => api.patchRequest(r.id, { priority: r.priority > 0 ? 0 : 1 }))}>
              {r.priority > 0 ? 'Clear priority' : 'Mark priority'}
            </button>
            <button className="wt-btn small secondary" disabled={busy} onClick={() => {
              const notes = window.prompt('Notes for this test', r.notes || '');
              if (notes !== null) act(() => api.patchRequest(r.id, { notes }));
            }}>Notes</button>
            <Link className="wt-btn small secondary" to={`/production/wick-test/${r.candidates?.[0]?.code || ''}`} target="_blank">Open in lab</Link>
            <button className="wt-btn small danger" disabled={busy} onClick={() => {
              if (window.confirm(`Cancel ${r.code}?`)) act(() => api.patchRequest(r.id, { status: 'cancelled' }));
            }}>Cancel</button>
          </div>
        </div>
      ))}
    </>
  );
}

function AddCandidate({ wicks, disabled, onAdd }) {
  const [open, setOpen] = useState(false);
  const [sku, setSku] = useState('');
  if (!open) return <button className="wt-btn small secondary" disabled={disabled} onClick={() => setOpen(true)}>Add candidate</button>;
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <select className="wt-select" value={sku} onChange={(e) => setSku(e.target.value)} style={{ maxWidth: 320 }}>
        <option value="">Pick a stocked wick…</option>
        {wicks.map((w) => <option key={w.sku} value={w.sku}>{w.sku.replace(/^wick-/i, '')}</option>)}
      </select>
      <button className="wt-btn small" disabled={!sku || disabled} onClick={() => { onAdd({ wick_sku: sku }); setSku(''); setOpen(false); }}>Add</button>
      <button className="wt-btn small secondary" onClick={() => setOpen(false)}>Close</button>
    </span>
  );
}

// ── Results ─────────────────────────────────────────────────────────────────

function ResultsTab({ requests }) {
  const rows = requests.filter((r) => ['approved', 'failed', 'cancelled'].includes(r.status))
    .sort((a, b) => String(b.approved_at || b.updated_at || '').localeCompare(String(a.approved_at || a.updated_at || '')));
  return (
    <div className="wt-table-wrap">
      <table className="wt-table">
        <thead>
          <tr>
            <th>Code</th><th>Status</th><th>Vessel</th><th>Fragrance</th><th>Size</th><th>Wax</th><th>Approved wick</th><th>Candidates</th><th>When</th><th>By</th><th>Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.code}</td>
              <td><span className={`wt-badge ${r.status}`}>{STATUS_LABEL[r.status]}</span></td>
              <td>{shortSku(r.vessel_sku)}</td>
              <td>{shortSku(r.oil_sku)}</td>
              <td>{r.size_label || ''}</td>
              <td>{shortWax(r.wax_sku)}</td>
              <td><strong>{r.approved_wick_sku ? shortSku(r.approved_wick_sku) : ''}</strong></td>
              <td>
                <div className="wt-cands">
                  {(r.candidates || []).map((c) => <span key={c.id} className="wt-cand"><span className="wt-cand-code">{c.code.split('-').pop()}</span>{c.wick_label}<span className={`wt-badge ${c.status}`}>{c.status.replace('_', ' ')}</span></span>)}
                </div>
              </td>
              <td>{fmtDate(r.approved_at || r.updated_at)}</td>
              <td>{r.approved_by || ''}</td>
              <td>{r.source_ref ? <a href={r.source_ref} target="_blank" rel="noreferrer" className="wt-link-btn">{r.source === 'drive_import' ? 'Drive sheet' : 'link'}</a> : (r.source || '')}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={11} className="wt-muted" style={{ padding: 20 }}>No results yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ── Settings ────────────────────────────────────────────────────────────────

function SettingsTab({ settings, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const out = await api.saveSettings(form);
      onSaved(out);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };
  const fields = [
    ['current_wax_sku', 'Current wax (item name)', 'text'],
    ['candidates_per_request', 'Candidates per test', 'number'],
    ['min_tests_to_approve', 'Minimum 4-hour tests before approval', 'number'],
    ['max_vessel_temp_glass_f', 'Max vessel temp, glass and ceramic (°F)', 'number'],
    ['max_vessel_temp_metal_f', 'Max vessel temp, metal (°F)', 'number'],
    ['max_flame_height_in', 'Max flame height (in)', 'number'],
    ['max_melt_pool_depth_in', 'Max melt pool depth (in)', 'number'],
    ['max_afterglow_s', 'Max afterglow (seconds)', 'number'],
    ['max_aftersmoke_s', 'Max aftersmoke (seconds)', 'number'],
  ];
  return (
    <div className="wt-card" style={{ maxWidth: 620 }}>
      <p className="wt-note">Changing the current wax makes every combo "not tested" again until it has an approved test on the new wax. Limits follow the Burn Quality Test sheet (ASTM F-2417).</p>
      <div className="wt-form-grid">
        {fields.map(([k, label, type]) => (
          <div className="wt-field" key={k}>
            <label>{label}</label>
            <input type={type} step="any" value={form[k] ?? ''} onChange={set(k)} />
          </div>
        ))}
      </div>
      {msg && <div className={/saved/i.test(msg) ? 'wt-ok' : 'wt-error'}>{msg}</div>}
      <div className="wt-modal-actions">
        <button className="wt-btn" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
      </div>
    </div>
  );
}
